import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../../contexts/GameContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SPECIAL_FISH_STYLES, TYPE_EFFECTS } from '../../constants/specialFish';
import MokugyoIcon from '../ui/MokugyoIcon';

const MokugyoContainer = () => {
  const { state, tapMokugyo } = useGame();
  const { t } = useLanguage();
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);
  const particlePoolRef = useRef([]);
  const animationFrameRef = useRef(null);
  const lastTapCountRef = useRef(0);

  const performTap = useCallback(() => {
    setIsAnimating(true);
    tapMokugyo();
    createVisualEffects();
    setTimeout(() => setIsAnimating(false), 150);
  }, [tapMokugyo]);

  const handleManualTap = useCallback(
    (e) => {
      if (e && e.cancelable) e.preventDefault();
      performTap();
    },
    [performTap]
  );

  useEffect(() => {
    if (state.autoTapActive && state.totalTaps !== lastTapCountRef.current) {
      lastTapCountRef.current = state.totalTaps;
      setIsAnimating(true);
      createVisualEffects();
      setTimeout(() => setIsAnimating(false), 150);
    }
  }, [state.totalTaps, state.autoTapActive]);

  useEffect(() => {
    if (!state.autoTapActive) {
      lastTapCountRef.current = state.totalTaps;
    }
  }, [state.autoTapActive, state.totalTaps]);

  const createVisualEffects = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const currentType = TYPE_EFFECTS[state.currentType];
    const particleCount = 2;

    for (let i = 0; i < particleCount; i++) {
      setTimeout(() => {
        const angle = Math.random() * Math.PI * 2;
        const radius = 50 + Math.random() * 30;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        createParticleBurst(x, y, currentType);
      }, i * 30);
    }
  };

  const createParticleBurst = (x, y, typeConfig) => {
    if (!containerRef.current) return;

    const particle =
      typeConfig.particles[Math.floor(Math.random() * typeConfig.particles.length)];

    let bubble = particlePoolRef.current.pop();
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'merit-bubble';
    }

    bubble.style.left = `${x}px`;
    bubble.style.top = `${y}px`;
    bubble.style.color = typeConfig.color;
    bubble.textContent = particle;
    bubble.style.willChange = 'transform, opacity';

    containerRef.current.appendChild(bubble);

    const flyAngle = Math.random() * Math.PI * 2;
    const flyDistance = 60 + Math.random() * 40;
    const endX = x + Math.cos(flyAngle) * flyDistance;
    const endY = y + Math.sin(flyAngle) * flyDistance - 40;

    const animation = bubble.animate(
      [
        { transform: 'translate(0, 0) scale(0.3) rotate(0deg)', opacity: 0 },
        {
          transform: `translate(${(endX - x) * 0.3}px, ${(endY - y) * 0.3}px) scale(1.2) rotate(180deg)`,
          opacity: 1,
          offset: 0.3
        },
        {
          transform: `translate(${endX - x}px, ${endY - y - 40}px) scale(0.5) rotate(360deg)`,
          opacity: 0
        }
      ],
      {
        duration: 1000,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    );

    animation.onfinish = () => {
      if (bubble.parentNode) {
        bubble.parentNode.removeChild(bubble);
      }
      if (particlePoolRef.current.length < 20) {
        particlePoolRef.current.push(bubble);
      }
    };
  };

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const currentFish = SPECIAL_FISH_STYLES[state.currentSpecialFish] || SPECIAL_FISH_STYLES.normal;
  const currentType = TYPE_EFFECTS[state.currentType];
  const typeName = t(`types.${state.currentType}.name`) || currentType.name;

  // 🐱 檢測是否為貓咪木魚
  const isCatFish = state.currentSpecialFish === 'cat';

  return (
    <div ref={containerRef} className="mokugyo-zone">
      <div
        className="mokugyo-glow"
        style={{
          background: currentType.glowColor,
          boxShadow: `0 0 80px 40px ${currentType.glowColor}`
        }}
      />

      <motion.div
        className="mokugyo-body"
        onPointerDown={handleManualTap}
        animate={isAnimating ? { scale: 0.92, y: 4 } : { scale: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
      >
        {/* 🐱 如果是貓咪木魚,使用 SVG 圖標 */}
        {isCatFish ? (
          <div className="mokugyo-cat-container">
            <MokugyoIcon type="cat" size={200} />
          </div>
        ) : (
          <>
            {/* 普通木魚外觀 */}
            <div
              className={`mokugyo-shell ${currentFish.className}`}
              style={{ background: currentFish.color }}
            >
              {/* 左眼 */}
              <div className="mokugyo-eye left">
                <motion.div
                  className="pupil"
                  animate={{ scaleY: [1, 0.1, 1] }}
                  transition={{ repeat: Infinity, duration: 4, times: [0, 0.95, 1] }}
                />
              </div>

              {/* 右眼 */}
              <div className="mokugyo-eye right">
                <motion.div
                  className="pupil"
                  animate={{ scaleY: [1, 0.1, 1] }}
                  transition={{ repeat: Infinity, duration: 4, times: [0, 0.95, 1] }}
                />
              </div>

              {/* 嘴巴 */}
              <div className="mokugyo-mouth" />
            </div>
          </>
        )}

        {/* 木槌 */}
        <motion.div
          className="mokugyo-mallet"
          animate={isAnimating ? { rotate: [-12, -40, -12], y: [0, 4, 0] } : { rotate: -12 }}
          transition={{ duration: 0.15 }}
        >
          <div className="mallet-stick" />
          <div className="mallet-head" />
        </motion.div>
      </motion.div>

      {/* 類型指示器 */}
      <motion.div
        className="type-indicator"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        key={state.currentType}
      >
        <span style={{ color: currentType.color }}>{currentType.icon}</span>
        <span style={{ color: currentType.color }}>{typeName}</span>
      </motion.div>
    </div>
  );
};

export default MokugyoContainer;